import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const districtKeys = {
  all: ['districts'],
  lists: () => [...districtKeys.all, 'list'],
  details: () => [...districtKeys.all, 'detail'],
  detail: (id) => [...districtKeys.details(), id],
};

export const useDistricts = () => {
  return useQuery({
    queryKey: districtKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('districts');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useDistrict = (id, options = {}) => {
  return useQuery({
    queryKey: districtKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`districts/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateDistrict = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (districtData) => {
      const response = await apiClient.post('districts', districtData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: districtKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating district:', error);
    }
  });
};

export const useUpdateDistrict = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (districtData) => {
      const response = await apiClient.put(`districts/${districtData.uuid}`, districtData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(districtKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: districtKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating district:', error);
    }
  });
};

export const useDeleteDistrict = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`districts/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: districtKeys.lists() });
      queryClient.removeQueries({ queryKey: districtKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting district:', error);
    }
  });
};